/**
 * @file HistoryLockedState.tsx
 * @brief B39 (28-10-2026): what a meeting, note or interpretation session
 *        detail shows on 403 HISTORY_LOCKED: the server's sentence, "Pick a
 *        plan" to Billing on the Free wording, and Back. No retry: trying
 *        again cannot help.
 */

import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/shared/auth/auth-context";
import { billingAudienceFor, billingErrorView } from "@/features/my-billing/billingErrors";
import { historyLockedServerMessage } from "./historyLock";

interface Props {
  error: unknown;
  onBack?: () => void;
  backLabel?: string;
}

export function HistoryLockedState({ error, onBack, backLabel = "Back" }: Props) {
  const { user } = useAuth();
  const view = billingErrorView("HISTORY_LOCKED", billingAudienceFor(user), historyLockedServerMessage(error));
  if (!view) return null;
  return (
    <div
      role="status"
      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-app-card p-8 text-center"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-app-quaternary text-app-text-secondary">
        <Lock className="h-5 w-5" aria-hidden />
      </div>
      <div className="space-y-1">
        <p className="text-[15px] font-semibold text-app-text">{view.title}</p>
        <p className="max-w-sm text-sm text-app-text-secondary">{view.message}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {view.action ? (
          <Button asChild size="sm">
            <Link to={view.action.to}>{view.action.label}</Link>
          </Button>
        ) : null}
        {onBack ? (
          <Button variant="outline" size="sm" onClick={onBack}>
            {backLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
